/**
 * The call outcomes a person picks from, in the order the form offers them.
 *
 * Shared because two screens open the same call dialogs -- the Daily Calls
 * Report and the dashboard's Recall list -- and a second copy of this list is
 * how one of them ends up offering an option the other does not.
 *
 * Distinct from usePositiveOutcomes, which is fetched: that one is a *rule*
 * the server also applies, so it has to come from the server. This is a
 * presentation order and a set of human labels, which the server has no
 * opinion about.
 *
 * **Grouped, and the grouping is the client's.** Follow up schedules a chase
 * and lands on Recall; Database does not, and a recruiter can still ring those
 * people whenever they like -- what they do not get is a task telling them to.
 * Removes takes the candidate out of the working lists altogether, which is
 * why those five sit apart and read as what they do.
 *
 * `other` is deliberately absent. 13,895 calls carry it and 12,381 of those
 * hold free text no pattern could classify, so it stays in the enum for those
 * rows to read and for the importer's terminal case -- but nothing new should
 * join them by hand, and a picker is how they would.
 *
 * `interested` left for the client's reasons rather than ours: Shortlisted is
 * the outcome that means this candidate is going forward, and an Interested
 * beside it was two words for one decision. It leaves the picker the way
 * `other` did -- 13,051 calls carry it, so it stays readable, stays
 * filterable, and stays in the enum. Nothing rewrites those rows: they record
 * what a recruiter chose at the time, and restating them as Shortlisted would
 * put words in their mouth.
 */
import type { CallOutcome } from '../types';

export interface OutcomeOption {
  value: CallOutcome;
  label: string;
  group: 'Follow up' | 'Database' | 'Shortlisted' | 'Removes the candidate';
}

export const OUTCOME_OPTIONS: OutcomeOption[] = [
  { value: 'shortlisted', label: 'Shortlisted', group: 'Shortlisted' },

  { value: 'call_back', label: 'Call Back', group: 'Follow up' },
  { value: 'info_sent', label: 'Sent JD Details', group: 'Follow up' },
  { value: 'awaiting_confirmation', label: 'Awaiting Confirmation', group: 'Follow up' },
  { value: 'yet_to_get_resume', label: 'Yet to get Resume', group: 'Follow up' },
  { value: 'sent_whatsapp', label: 'Sent WhatsApp Message', group: 'Follow up' },
  { value: 'disconnected', label: 'Disconnected', group: 'Follow up' },
  { value: 'busy', label: 'Number Busy', group: 'Follow up' },

  { value: 'screen_reject', label: 'Screen Reject', group: 'Database' },
  { value: 'documents_issue', label: 'Documents Issue', group: 'Database' },
  { value: 'not_picking', label: 'Not Responding Calls', group: 'Database' },
  { value: 'switched_off', label: 'Switched Off', group: 'Database' },
  { value: 'not_interested', label: 'Not Interested', group: 'Database' },
  { value: 'not_looking', label: 'Not Looking for Job', group: 'Database' },
  { value: 'already_working', label: 'Already Working', group: 'Database' },
  { value: 'location_issue', label: 'Location Issue', group: 'Database' },
  { value: 'salary_issue', label: 'Salary Issue', group: 'Database' },
  { value: 'notice_period', label: 'Notice Period Too Long', group: 'Database' },
  { value: 'asked_for_referral', label: 'Asked for Referral', group: 'Database' },
  { value: 'language_barrier', label: 'Language Barrier', group: 'Database' },

  { value: 'wrong_profile', label: 'Wrong Profile', group: 'Removes the candidate' },
  { value: 'out_of_service', label: 'Out of Service', group: 'Removes the candidate' },
  { value: 'not_suitable', label: 'Not Suitable', group: 'Removes the candidate' },
  { value: 'invalid_number', label: 'Invalid Number', group: 'Removes the candidate' },
  {
    value: 'too_many_tried',
    label: 'Too Many Times Tried, No Response',
    group: 'Removes the candidate',
  },
];

/** The order the groups are offered in. */
export const OUTCOME_GROUPS: OutcomeOption['group'][] = [
  'Shortlisted',
  'Follow up',
  'Database',
  'Removes the candidate',
];

/**
 * The flat shape the dialogs already take.
 *
 * Kept so the pickers can stay ungrouped where grouping would not help, and so
 * this file has one exported order rather than two that can disagree.
 */
export const OUTCOMES: { value: CallOutcome; label: string }[] =
  OUTCOME_OPTIONS.map(({ value, label }) => ({ value, label }));

/**
 * Whether this outcome leaves a chase behind.
 *
 * Read off the client's own grouping rather than from the server's
 * FOLLOWUP_DELAYS, because the question being asked is theirs: the row action
 * says "Follow up" only where they classified the outcome as Follow up, and
 * "Log call" on everything they called Data Base. Deriving it from the
 * scheduling table would answer a subtly different question and drift from the
 * labels in the same file.
 *
 * False for anything not in the list, which is the three legacy values --
 * `other`, `hold` and `missing_requirement`. That is right for two of them.
 * `hold` does still schedule until the migration retires it, so for now its
 * 217 rows read "Log call" while a chase exists; it is a word on a button
 * rather than a behaviour, and it resolves itself when those rows become Call
 * Back.
 */
const FOLLOW_UP_VALUES = new Set<CallOutcome>(
  OUTCOME_OPTIONS.filter((o) => o.group === 'Follow up').map((o) => o.value),
);

export function schedulesFollowUp(outcome: CallOutcome): boolean {
  return FOLLOW_UP_VALUES.has(outcome);
}

/**
 * Retired from the picker but still readable.
 *
 * A row carrying one of these has to render as something, and falling through
 * to the raw value would put `missing_requirement` on screen in a column of
 * sentences. OUTCOME_LABELS spreads these, so a table reads them as sentences
 * without a picker offering them.
 *
 * Declared above OUTCOME_LABELS rather than below it because that constant
 * now reads this one. A `const` referenced before its initialiser runs is a
 * ReferenceError at import, not a warning.
 */
export const LEGACY_OUTCOME_LABELS: Record<string, string> = {
  interested: 'Interested',
  other: 'Other',
  hold: 'Hold',
  missing_requirement: 'Missing Requirement',
};

/**
 * Same labels, by value — so a warning reads like the form.
 *
 * Retired values included, because a table renders what a row holds and not
 * what the picker offers. Without them 13,051 Interested calls and 110,377
 * `other` ones fall through to their raw enum value.
 */
export const OUTCOME_LABELS: Record<string, string> = {
  ...Object.fromEntries(OUTCOME_OPTIONS.map((o) => [o.value, o.label])),
  ...LEGACY_OUTCOME_LABELS,
};

/**
 * Every outcome a filter should offer: what can be chosen, plus what has been.
 *
 * A retired outcome that cannot be filtered for is 13,051 calls nobody can
 * find. Taking a value out of the *picker* says "do not record this any
 * more"; taking it out of the *filter* says "these rows are not there", which
 * is a different statement and a false one.
 */
export const OUTCOME_FILTER_OPTIONS: { value: string; label: string }[] = [
  ...OUTCOMES,
  ...Object.entries(LEGACY_OUTCOME_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

/**
 * The picker's options, plus whatever this row already holds.
 *
 * An edit dialog offering a list without the stored value shows an empty
 * select over a call that has an outcome, and the first nudge of the keyboard
 * rewrites it to whichever option the browser lands on. Any screen editing an
 * existing call asks for this rather than the bare list.
 */
export function withStoredOutcome(
  options: { value: CallOutcome; label: string }[],
  stored: CallOutcome | null | undefined,
): { value: CallOutcome; label: string }[] {
  if (!stored || options.some((o) => o.value === stored)) return options;
  return [...options, { value: stored, label: OUTCOME_LABELS[stored] ?? stored }];
}

